import React, { useState, useEffect } from "react";
import { FaCheck, FaEye, FaEyeSlash } from "react-icons/fa";
import { validatePassword } from "../../utils/validation";
import {
  sanitizeInput,
  sanitizeEmail,
  sanitizePhone,
} from "../../utils/sanitization";
import { formatPhoneNumberAsTyped } from "../../utility/forms/formInputFormat";
import {
  collectSignupFieldErrors,
  visibleSignupFieldErrors,
} from "../../utility/signupFormValidation";

const SignupFormFields = ({
  formData,
  setFormData,
  errors,
  setErrors,
  loading,
  showPassword,
  setShowPassword,
  showConfirmPassword,
  setShowConfirmPassword,
  passwordChecks,
  setPasswordChecks,
  onInputChange,
  onInputBlur,
  onSubmit,
  submitButtonText = "Create Account",
  submitButtonIcon = <FaCheck className="mr-2" />,
  showTerms = true,
  acceptedTerms,
  setAcceptedTerms,
  showCaptcha = false,
  captchaToken,
  onCaptchaError,
  onCaptchaExpire,
  onCaptchaVerify,
  children, // For additional fields like Turnstile
}) => {
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    if (formData.password) {
      const passwordValidation = validatePassword(formData.password);
      if (passwordValidation?.checks) {
        setPasswordChecks(passwordValidation.checks);
      }
    }

    const allErrors = collectSignupFieldErrors(formData, {
      showTerms,
      acceptedTerms,
    });
    setErrors((prev) => {
      const visible = visibleSignupFieldErrors(allErrors, {
        touched,
        submitAttempted,
      });
      if (prev.submit) {
        visible.submit = prev.submit;
      }
      return visible;
    });
  }, [
    formData,
    touched,
    submitAttempted,
    showTerms,
    acceptedTerms,
    setPasswordChecks,
    setErrors,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Special handling for phone numbers - store only digits
    if (name === "phone") {
      processedValue = value.replace(/\D/g, ""); // Remove all non-digits
    }

    // Update form data without sanitization (for real-time typing)
    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]: processedValue,
      };

      return newData;
    });

    // Call custom input change handler if provided
    if (onInputChange) {
      onInputChange(e);
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    let sanitizedValue = value;

    // Apply appropriate sanitization based on field type
    switch (name) {
      case "email":
        sanitizedValue = sanitizeEmail(value);
        break;
      case "phone":
        sanitizedValue = sanitizePhone(value);
        break;
      case "displayName":
        sanitizedValue = sanitizeInput(value);
        break;
      default:
        sanitizedValue = value;
    }

    const nextFormData =
      sanitizedValue !== value
        ? { ...formData, [name]: sanitizedValue }
        : formData;

    if (sanitizedValue !== value) {
      setFormData((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    }

    const allErrors = collectSignupFieldErrors(nextFormData, {
      showTerms,
      acceptedTerms,
    });
    setErrors((prev) => {
      const next = { ...prev };
      if (allErrors[name]) {
        next[name] = allErrors[name];
      } else {
        delete next[name];
      }
      return next;
    });

    // Call custom input blur handler if provided
    if (onInputBlur) {
      onInputBlur(e);
    }
  };

  const validateForm = () => {
    setSubmitAttempted(true);
    const newErrors = collectSignupFieldErrors(formData, {
      showTerms,
      acceptedTerms,
    });

    setErrors((prev) => ({
      ...(prev.submit ? { submit: prev.submit } : {}),
      ...newErrors,
    }));
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm() && onSubmit) {
      onSubmit(e);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" noValidate>
      {/* Display Name */}
      <div>
        <label
          htmlFor="displayName"
          className="block text-sm font-medium text-gray-700"
        >
          Name
        </label>
        <div className="mt-1">
          <input
            id="displayName"
            name="displayName"
            type="text"
            required
            value={formData.displayName}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`appearance-none block w-full px-3 py-2 border ${
              errors.displayName ? "border-red-300" : "border-gray-300"
            } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-brand-gold-ink focus:border-brand-gold-ink`}
            placeholder="Enter your full name"
            disabled={loading}
          />
          {errors.displayName && (
            <p className="mt-1 text-sm text-red-600">{errors.displayName}</p>
          )}
        </div>
      </div>

      {/* Email */}
      <div>
        <label
          htmlFor="email"
          className="block text-sm font-medium text-gray-700"
        >
          Email Address
        </label>
        <div className="mt-1">
          <input
            id="email"
            name="email"
            type="email"
            required
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`appearance-none block w-full px-3 py-2 border ${
              errors.email ? "border-red-300" : "border-gray-300"
            } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-brand-gold-ink focus:border-brand-gold-ink`}
            placeholder="Enter your email address"
            disabled={loading}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email}</p>
          )}
        </div>
      </div>

      {/* Phone (Optional) */}
      <div>
        <label
          htmlFor="phone"
          className="block text-sm font-medium text-gray-700"
        >
          Phone Number (Optional)
        </label>
        <div className="mt-1">
          <input
            id="phone"
            name="phone"
            type="tel"
            value={formatPhoneNumberAsTyped(formData.phone)}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`appearance-none block w-full px-3 py-2 border ${
              errors.phone ? "border-red-300" : "border-gray-300"
            } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-brand-gold-ink focus:border-brand-gold-ink`}
            placeholder="(123) 456-7890"
            disabled={loading}
          />
          {errors.phone && (
            <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
          )}
        </div>
      </div>

      {/* Password */}
      <div>
        <label
          htmlFor="password"
          className="block text-sm font-medium text-gray-700"
        >
          Password
        </label>
        <div className="mt-1">
          <div className="relative">
            <input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              required
              value={formData.password}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`appearance-none block w-full px-3 py-2 pr-10 border ${
                errors.password ? "border-red-300" : "border-gray-300"
              } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-brand-gold-ink focus:border-brand-gold-ink`}
              placeholder="Create a password"
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center focus:outline-none focus:ring-2 focus:ring-brand-gold-ink rounded"
              aria-label={showPassword ? "Hide password" : "Show password"}
              disabled={loading}
            >
              {showPassword ? (
                <FaEyeSlash className="h-4 w-4 text-gray-400" />
              ) : (
                <FaEye className="h-4 w-4 text-gray-400" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Password Requirements */}
      {formData.password && (
        <div className="bg-gray-50 p-3 rounded-md">
          <p className="text-sm font-medium text-gray-700 mb-2">
            Password Requirements:
          </p>
          <ul className="text-sm space-y-1">
            <li
              className={`flex items-center ${
                passwordChecks.length ? "text-brand-charcoal" : "text-gray-500"
              }`}
            >
              <FaCheck
                className={`h-3 w-3 mr-2 ${
                  passwordChecks.length ? "text-brand-charcoal" : "text-gray-400"
                }`}
              />
              At least 8 characters
            </li>
            <li
              className={`flex items-center ${
                passwordChecks.uppercase ? "text-brand-charcoal" : "text-gray-500"
              }`}
            >
              <FaCheck
                className={`h-3 w-3 mr-2 ${
                  passwordChecks.uppercase ? "text-brand-charcoal" : "text-gray-400"
                }`}
              />
              One uppercase letter
            </li>
            <li
              className={`flex items-center ${
                passwordChecks.lowercase ? "text-brand-charcoal" : "text-gray-500"
              }`}
            >
              <FaCheck
                className={`h-3 w-3 mr-2 ${
                  passwordChecks.lowercase ? "text-brand-charcoal" : "text-gray-400"
                }`}
              />
              One lowercase letter
            </li>
            <li
              className={`flex items-center ${
                passwordChecks.number ? "text-brand-charcoal" : "text-gray-500"
              }`}
            >
              <FaCheck
                className={`h-3 w-3 mr-2 ${
                  passwordChecks.number ? "text-brand-charcoal" : "text-gray-400"
                }`}
              />
              One number
            </li>
            <li
              className={`flex items-center ${
                passwordChecks.special ? "text-brand-charcoal" : "text-gray-500"
              }`}
            >
              <FaCheck
                className={`h-3 w-3 mr-2 ${
                  passwordChecks.special ? "text-brand-charcoal" : "text-gray-400"
                }`}
              />
              One special character
            </li>
          </ul>
        </div>
      )}

      {/* Confirm Password */}
      <div>
        <label
          htmlFor="confirmPassword"
          className="block text-sm font-medium text-gray-700"
        >
          Confirm Password
        </label>
        <div className="mt-1">
          <div className="relative">
            <input
              id="confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              required
              value={formData.confirmPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`appearance-none block w-full px-3 py-2 pr-10 border ${
                errors.confirmPassword ? "border-red-300" : "border-gray-300"
              } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-brand-gold-ink focus:border-brand-gold-ink`}
              placeholder="Confirm your password"
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center focus:outline-none focus:ring-2 focus:ring-brand-gold-ink rounded"
              aria-label={
                showConfirmPassword
                  ? "Hide confirm password"
                  : "Show confirm password"
              }
              disabled={loading}
            >
              {showConfirmPassword ? (
                <FaEyeSlash className="h-4 w-4 text-gray-400" />
              ) : (
                <FaEye className="h-4 w-4 text-gray-400" />
              )}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="mt-1 text-sm text-red-600">
              {errors.confirmPassword}
            </p>
          )}
        </div>
      </div>

      {/* Terms and Conditions */}
      {showTerms && (
        <div>
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="terms"
                name="terms"
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="focus:ring-brand-gold-ink h-4 w-4 text-brand-gold-ink border-gray-300 rounded"
                disabled={loading}
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="terms" className="text-gray-700">
                I agree to the{" "}
                <a
                  href="/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-gold-ink hover:text-brand-charcoal"
                >
                  Terms and Conditions
                </a>{" "}
                and{" "}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-gold-ink hover:text-brand-charcoal"
                >
                  Privacy Policy
                </a>
              </label>
              {errors.terms && (
                <p className="mt-1 text-sm text-red-600">{errors.terms}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Additional children (like CAPTCHA) */}
      {children}

      {/* Submit Button */}
      <div>
        <button
          type="submit"
          disabled={loading || Object.keys(errors).filter(key => key !== 'submit').length > 0}
          className="w-full flex justify-center py-2 px-4 rounded-lg text-sm font-display font-bold tracking-wide text-brand-charcoal bg-brand-gold hover:bg-brand-gold/90 focus:outline-none focus:ring-2 focus:ring-brand-gold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-brand-charcoal"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Creating Account...
            </>
          ) : (
            <>
              {submitButtonIcon}
              {submitButtonText}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default SignupFormFields;
